/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import axios from 'axios';
/**
 * Input for get-categories data action.
 */
export class GetAddressSuggestionsInput implements IActionInput {
    public query?: string;
    public GetAddressSuggestionsInput(query?: string) {
        this.query = query;
    }

    public getCacheKey = () => `getAddressSuggestions`;
    public getCacheObjectType = () => 'getAddressSuggestionsState';
    public dataCacheType = (): CacheType => 'request';
}

export interface IAddressSuggestions {
    id: string;
    count: number;
    labels: string[];
}
interface IFindAPIResponseResults {
    results: IAddressSuggestions[];
}
interface IFindAPIResponse {
    data: IFindAPIResponseResults;
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- Input data.
 * @returns -- IActionInput.
 */
export const createGetAddressSuggestionsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetAddressSuggestionsInput();
};

/**
 * Calls the Retail API and returns all the categories as a flat list.
 * @param input - Input.
 * @param ctx - Context.
 * @returns - CategoryHierarchy.
 */
export async function getAddressSuggestionsAction(input: GetAddressSuggestionsInput, ctx: IActionContext): Promise<IAddressSuggestions[]> {
    const addressSuggestions: IAddressSuggestions[] = [];
    const headers = {
        'Content-Type': 'application/json'
    };
    // TODO Get values from secrets
    // const fetchifyKeySecret: ISecretValue | undefined = await msdyn365Commerce.secretManager?.getSecretValue('FetchifyKey', ctx.requestContext.apiSettings.baseUrl);
    // const fetchifyBaseURL: ISecretValue | undefined = await msdyn365Commerce.secretManager?.getSecretValue('FetchifyAddressURL', ctx.requestContext.apiSettings.baseUrl);
    const suggestionsResponse = await axios({
        method: 'POST',
        url: `https://api.craftyclicks.co.uk/address/1.1/find`, // TODO : Get value from secrets`${fetchifyBaseURL?.value}find`,//,
        data: {
            key: '1024c-01f0c-70dd6-06464', // TODO : Get value from secrets fetchifyKeySecret?.value, //,
            query: input.query,
            country: 'gbr',
            id: '',
            extra: {
                exclude_areas: ['gbr_channel_islands', 'usa_non_contiguous'],
                exclude_pobox: true,
                best_match_only: false,
                no_groupings: true
            }
        },
        headers: headers
    }).catch(error => {
        ctx.telemetry.error(error);
    });
    const findAPIResponse = (suggestionsResponse as unknown) as IFindAPIResponse;
    if (Array.isArray(findAPIResponse.data.results)) {
        for (const res of findAPIResponse.data.results) {
            addressSuggestions.push({ id: res.id, count: res.count, labels: res.labels });
        }
    }

    return addressSuggestions;
}

/**
 * The getCategory Data Action.
 */
export const getAddressSuggestionsDataAction = createObservableDataAction({
    id: 'dobbies-get-address-suggestions',
    action: getAddressSuggestionsAction as IAction<IAddressSuggestions[]>,
    input: createGetAddressSuggestionsInput
});

export default getAddressSuggestionsAction;
